import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Card, CardContent } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  AlertCircle,
  Loader2,
  Plus,
  Trash2,
} from 'lucide-react';
import workOrderService from '../../services/productionService';
import warehouseService from '../../services/warehouseService';

const CompleteWorkOrderDialog = ({
  open,
  onClose,
  workOrder,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [linkedMovements, setLinkedMovements] = useState([]);

  const [actualMaterialsUsed, setActualMaterialsUsed] = useState(null);
  const [defaultLocation, setDefaultLocation] = useState('main');
  
  const [productCompletions, setProductCompletions] = useState([]);
  
  const [remainingBars, setRemainingBars] = useState([]);
  const [showAddRemaining, setShowAddRemaining] = useState(false);
  const [newRemainingBar, setNewRemainingBar] = useState({
    material_code: '',
    material_name: '',
    length: '',
    quantity: 1,
    location: 'main',
    product_id: ''
  });

  const availableMaterials = useMemo(() => {
    if (linkedMovements && linkedMovements.length > 0) {
      const materials = new Map();

      linkedMovements.forEach(move => {
        if (move.products && Array.isArray(move.products)) {
          move.products.forEach(prod => {
            const code = prod.product_id || prod.material_product_code || prod.product_code;
            const name = prod.product_name || prod.material_product_name || '';
            const id = prod._id || prod.material_product_id || prod.product_id;

            if (code && !materials.has(code)) {
              materials.set(code, {
                code,
                name,
                id: id || '',
                product_code: prod.finished_product_code || ''
              });
            }
          });
        }
      });

      if (materials.size > 0) {
        return Array.from(materials.values());
      }
    }

    if (workOrder?.items) {
      const materials = new Map();
      workOrder.items.forEach(item => {
        if (item.material_requirements && Array.isArray(item.material_requirements)) {
          item.material_requirements.forEach(mat => {
            const code = mat.material_product_code || mat.material_code;
            const id = mat.material_product_id || mat._id;
            if (code && !materials.has(code)) {
              materials.set(code, {
                code,
                name: mat.material_name || mat.material_product_name || '',
                id: id || '',
                product_code: item.finished_product_code || mat.product_code || ''
              });
            }
          });
        }
      });
      return Array.from(materials.values());
    }

    return [];
  }, [linkedMovements, workOrder]);

  const totalCompleted = useMemo(() => {
    if (workOrder?.items && workOrder.items.length > 0) {
      let completed = 0;
      workOrder.items.forEach(item => {
        if (item.process_steps && item.process_steps.length > 0) {
          const steps = item.process_steps;
          const lastStep = steps.reduce((max, s) => s.sequence > max.sequence ? s : max, { sequence: -1, quantity_completed: 0 });
          completed += Number(lastStep.quantity_completed || 0);
        }
      });
      return completed;
    }

    const history = workOrder?.progress_history || [];
    const completed = history.reduce((sum, entry) => sum + (entry.completed_quantity || 0), 0);
    return completed || Number(workOrder?.completed_quantity || 0);
  }, [workOrder]);

  const loadData = useCallback(async () => {
    if (!workOrder) return;
    setLoading(true);
    setError(null);
    try {
      if (workOrder.items && workOrder.items.length > 0) {
        const initialCompletions = workOrder.items.map((item, idx) => ({
          item_index: idx,
          product_code: item.finished_product_code,
          product_name: item.finished_product_name,
          specification: item.product_details?.specification || item.specification || '',
          planned_quantity: item.planned_quantity || 0,
          quantity: item.planned_quantity || 0,
          location: 'main',
          use_custom_location: false
        }));
        setProductCompletions(initialCompletions);
      }

      try {
        let movementIds = workOrder.material_issue_movements || [];
        if (typeof movementIds === 'object' && !Array.isArray(movementIds)) {
          const values = Object.values(movementIds).filter(v => v);
          movementIds = values.flat().filter(v => v && typeof v === 'string');
        }
        
        if (movementIds.length > 0) {
          const movements = await warehouseService.getMovementsByIds(movementIds);
          setLinkedMovements(movements);
        } else {
          setLinkedMovements([]);
        }
      } catch (err) {
        console.error('Failed to load movements:', err);
        setLinkedMovements([]);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('加载数据失败');
    } finally {
      setLoading(false);
    }
  }, [workOrder]);

  useEffect(() => {
    if (open && workOrder) {
      loadData();
      setRemainingBars([]);
      setDefaultLocation('main');
      setActualMaterialsUsed(null);
    }
  }, [open, workOrder, loadData]);

  const updateProductQuantity = (itemIndex, quantity) => {
    setProductCompletions(prev => 
      prev.map(p => p.item_index === itemIndex ? { ...p, quantity: parseFloat(quantity) || 0 } : p)
    );
  };

  const updateProductLocation = (itemIndex, location) => {
    setProductCompletions(prev => 
      prev.map(p => p.item_index === itemIndex ? { ...p, location } : p)
    );
  };

  const toggleCustomLocation = (itemIndex, useCustom) => {
    setProductCompletions(prev => 
      prev.map(p => p.item_index === itemIndex ? { 
        ...p, 
        use_custom_location: useCustom,
        location: useCustom ? p.location : defaultLocation
      } : p)
    );
  };

  const handleAddRemainingBar = () => {
    if (!newRemainingBar.material_code) {
      setError('请选择物料类型');
      return;
    }
    if (!newRemainingBar.length || parseFloat(newRemainingBar.length) <= 0) {
      setError('请输入有效的余料长度');
      return;
    }

    const selectedMaterial = availableMaterials.find(
      m => m.code === newRemainingBar.material_code
    );

    setRemainingBars([...remainingBars, {
      ...newRemainingBar,
      id: Date.now(),
      material_code: newRemainingBar.material_code,
      material_name: selectedMaterial?.name || '',
      product_code: selectedMaterial?.product_code || '',
      product_id: selectedMaterial?.id || '',
      length: parseFloat(newRemainingBar.length),
      quantity: parseInt(newRemainingBar.quantity) || 1
    }]);

    setNewRemainingBar({
      material_code: '',
      material_name: '',
      length: '',
      quantity: 1,
      location: 'main',
      product_id: ''
    });
    setShowAddRemaining(false);
    setError(null);
  };

  const removeRemainingBar = (id) => {
    setRemainingBars(remainingBars.filter(bar => bar.id !== id));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const calculatedTotal = productCompletions.reduce((sum, p) => sum + (parseFloat(p.quantity) || 0), 0);
      
      const productQuantities = productCompletions
        .filter(p => parseFloat(p.quantity) > 0)
        .map(p => ({
          item_index: p.item_index,
          quantity: parseFloat(p.quantity),
          location: p.use_custom_location ? p.location : defaultLocation,
          specification: p.specification || ''
        }));

      const result = await workOrderService.completeProduction(
        workOrder._id,
        calculatedTotal,
        0,
        '',
        actualMaterialsUsed ? parseFloat(actualMaterialsUsed) : null,
        remainingBars,
        productQuantities,
        defaultLocation
      );
      
      onSuccess?.(result);
      handleClose();
    } catch (err) {
      console.error('Failed to complete work order:', err);
      const errorMsg = err.response?.data?.error || err.response?.data?.message || '完成工单失败';
      setError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setRemainingBars([]);
    onClose();
  };

  const plannedQty = workOrder?.total_planned_quantity || workOrder?.planned_quantity || 0;
  const calculatedTotal = productCompletions.reduce((sum, p) => sum + (parseFloat(p.quantity) || 0), 0);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>完成工单</DialogTitle>
          <DialogDescription>
            确认工单 {workOrder?.production_order_id} 的完成信息
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label className="text-muted-foreground">工序完成数量</Label>
                <div className="text-lg font-semibold">{totalCompleted}</div>
              </div>
              <div className="space-y-1">
                <Label className="text-muted-foreground">计划数量</Label>
                <div className="text-lg font-semibold">{plannedQty}</div>
              </div>
              <div className="space-y-1">
                <Label className="text-muted-foreground">本次入库总数</Label>
                <div className={`text-lg font-semibold ${calculatedTotal !== totalCompleted ? 'text-amber-600' : ''}`}>
                  {calculatedTotal}
                  {calculatedTotal !== totalCompleted && (
                    <span className="text-xs ml-2 font-normal">(与工序完成数不一致)</span>
                  )}
                </div>
              </div>
            </div>

            {/* Product Completion Table */}
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-medium">成品入库明细</Label>
                    <div className="flex items-center gap-2">
                      <Label className="text-sm text-muted-foreground">默认入库位置:</Label>
                      <Input
                        value={defaultLocation}
                        onChange={(e) => {
                          const newDefault = e.target.value;
                          setDefaultLocation(newDefault);
                          setProductCompletions(prev => 
                            prev.map(p => p.use_custom_location ? p : { ...p, location: newDefault })
                          );
                        }}
                        className="w-32 h-8"
                        placeholder="默认位置"
                        disabled={submitting}
                      />
                    </div>
                  </div>

                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>产品代码</TableHead>
                        <TableHead>产品名称</TableHead>
                        <TableHead className="text-right">计划数量</TableHead>
                        <TableHead className="text-right">入库数量</TableHead>
                        <TableHead className="w-[100px]">自定义位置</TableHead>
                        <TableHead>入库位置</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {productCompletions.map((product) => (
                        <TableRow key={product.item_index}>
                          <TableCell className="font-mono text-sm">{product.product_code}</TableCell>
                          <TableCell className="text-sm">{product.product_name}</TableCell>
                          <TableCell className="text-right">{product.planned_quantity}</TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min="0"
                              step="1"
                              value={product.quantity}
                              onChange={(e) => updateProductQuantity(product.item_index, e.target.value)}
                              className="w-24 h-8 text-right"
                              disabled={submitting}
                            />
                          </TableCell>
                          <TableCell>
                            <Checkbox
                              checked={product.use_custom_location}
                              onCheckedChange={(checked) => toggleCustomLocation(product.item_index, checked)}
                              disabled={submitting}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={product.location}
                              onChange={(e) => updateProductLocation(product.item_index, e.target.value)}
                              className="w-32 h-8"
                              disabled={submitting || !product.use_custom_location}
                              placeholder={defaultLocation}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>实际使用物料数量</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.1"
                    value={actualMaterialsUsed || ''}
                    onChange={(e) => setActualMaterialsUsed(e.target.value)}
                    placeholder="手动输入实际消耗（可选）"
                    disabled={submitting}
                  />
                  <p className="text-xs text-muted-foreground">
                    如不填写则不记录实际消耗
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base">余料录入</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAddRemaining(true)}
                    disabled={submitting || showAddRemaining}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    添加余料
                  </Button>
                </div>

                {showAddRemaining && (
                  <Card className="p-3 border-dashed">
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <Label className="text-xs">选择物料</Label>
                        <Select
                          value={newRemainingBar.material_code}
                          onValueChange={(value) => {
                            const material = availableMaterials.find(m => m.code === value);
                            setNewRemainingBar({
                              ...newRemainingBar,
                              material_code: value,
                              material_name: material?.name || '',
                              product_code: material?.product_code || ''
                            });
                          }}
                        >
                          <SelectTrigger className="h-8">
                            <SelectValue placeholder="选择物料类型" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableMaterials.map((material) => (
                              <SelectItem key={material.code} value={material.code}>
                                {material.code} - {material.name} ({material.product_code})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs">长度(mm)</Label>
                          <Input
                            type="number"
                            value={newRemainingBar.length}
                            onChange={(e) => setNewRemainingBar({...newRemainingBar, length: e.target.value})}
                            placeholder="长度"
                            className="h-8"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">数量(根)</Label>
                          <Input
                            type="number"
                            value={newRemainingBar.quantity}
                            onChange={(e) => setNewRemainingBar({...newRemainingBar, quantity: e.target.value})}
                            placeholder="数量"
                            className="h-8"
                          />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">位置</Label>
                        <Input
                          value={newRemainingBar.location}
                          onChange={(e) => setNewRemainingBar({...newRemainingBar, location: e.target.value})}
                          placeholder="位置"
                          className="h-8"
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setShowAddRemaining(false)}>取消</Button>
                        <Button size="sm" onClick={handleAddRemainingBar}>确认添加</Button>
                      </div>
                    </div>
                  </Card>
                )}

                <div className="border rounded-md overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>物料</TableHead>
                        <TableHead>长度</TableHead>
                        <TableHead>数量</TableHead>
                        <TableHead>位置</TableHead>
                        <TableHead className="w-[50px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {remainingBars.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground text-sm py-4">
                            无余料记录
                          </TableCell>
                        </TableRow>
                      ) : (
                        remainingBars.map((bar) => (
                          <TableRow key={bar.id}>
                            <TableCell className="font-medium">{bar.material_code}</TableCell>
                            <TableCell>{bar.length}mm</TableCell>
                            <TableCell>{bar.quantity}</TableCell>
                            <TableCell>{bar.location}</TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive"
                                onClick={() => removeRemainingBar(bar.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={handleClose} disabled={submitting}>
                取消
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || calculatedTotal === 0}
              >
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                完成工单
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CompleteWorkOrderDialog;
